import { useEffect, useState } from 'react';
import { PiClock } from 'react-icons/pi';
import { useI18n } from '@v2/providers/I18nProvider';
import { cn } from '@v2/utils/cn';

type Props = {
    reservedAt: string | null | undefined;
    expiresAt: string | null | undefined;
    /** Se false, mostra estado esgotado sem tick. */
    active?: boolean;
    size?: 'sm' | 'md' | 'lg';
    className?: string;
};

type Snapshot = {
    remainingMs: number;
    totalMs: number;
    /** Progresso decorrido (0 → 100 até o prazo). */
    pctElapsed: number;
    expired: boolean;
};

function parseMs(value: string | null | undefined): number | null {
    if (!value) return null;
    const ms = new Date(value).getTime();
    return Number.isFinite(ms) ? ms : null;
}

function compute(reservedAt: string | null | undefined, expiresAt: string | null | undefined): Snapshot | null {
    const end = parseMs(expiresAt);
    if (end == null) return null;

    const start = parseMs(reservedAt) ?? end - 7 * 24 * 60 * 60 * 1000;
    const totalMs = Math.max(1, end - start);
    const remainingMs = end - Date.now();
    const elapsedMs = Date.now() - start;
    const pctElapsed = Math.max(0, Math.min(100, (elapsedMs / totalMs) * 100));

    return {
        remainingMs,
        totalMs,
        pctElapsed,
        expired: remainingMs <= 0,
    };
}

function formatRemaining(ms: number, t: (key: string, params?: Record<string, string | number>) => string): string {
    if (ms <= 0) {
        return t('v2.stock_approvals.countdown_expired');
    }

    const totalSec = Math.floor(ms / 1000);
    const days = Math.floor(totalSec / 86400);
    const hours = Math.floor((totalSec % 86400) / 3600);
    const minutes = Math.floor((totalSec % 3600) / 60);
    const seconds = totalSec % 60;

    if (days > 0) {
        return t('v2.stock_approvals.countdown_dh', { days, hours });
    }
    if (hours > 0) {
        return t('v2.stock_approvals.countdown_hm', { hours, minutes });
    }
    if (minutes > 0) {
        return t('v2.stock_approvals.countdown_ms', { minutes, seconds });
    }

    return t('v2.stock_approvals.countdown_s', { seconds });
}

/** Cores pelo progresso decorrido: no início (pouco preenchido) ok; perto do fim (cheio) urgente. */
function barTone(pctElapsed: number, expired: boolean): string {
    if (expired || pctElapsed >= 100) return 'bg-rose-500';
    if (pctElapsed >= 85) return 'bg-rose-500';
    if (pctElapsed >= 65) return 'bg-amber-500';
    return 'bg-emerald-500';
}

function trackTone(pctElapsed: number, expired: boolean): string {
    if (expired || pctElapsed >= 85) return 'bg-rose-100 dark:bg-rose-500/20';
    if (pctElapsed >= 65) return 'bg-amber-100 dark:bg-amber-500/20';
    return 'bg-emerald-100 dark:bg-emerald-500/20';
}

export default function ReservationExpiryCountdown({
    reservedAt,
    expiresAt,
    active = true,
    size = 'md',
    className,
}: Props) {
    const { t } = useI18n();
    const [snap, setSnap] = useState(() => compute(reservedAt, expiresAt));

    useEffect(() => {
        setSnap(compute(reservedAt, expiresAt));
        if (!active || !expiresAt) return;

        const id = window.setInterval(() => {
            setSnap(compute(reservedAt, expiresAt));
        }, 1000);

        return () => window.clearInterval(id);
    }, [reservedAt, expiresAt, active]);

    if (!snap || !expiresAt) {
        return null;
    }

    const expired = !active || snap.expired;
    const pct = expired ? 100 : snap.pctElapsed;
    const label = expired
        ? t('v2.stock_approvals.countdown_expired')
        : formatRemaining(snap.remainingMs, t);

    const barH = size === 'lg' ? 'h-3' : size === 'sm' ? 'h-1.5' : 'h-2';
    const labelCls =
        size === 'lg'
            ? 'text-sm font-semibold'
            : size === 'sm'
              ? 'text-[11px] font-medium'
              : 'text-xs font-semibold';
    const urgent = expired || pct >= 65;

    return (
        <div
            className={cn(
                'rounded-xl border px-3 py-2.5',
                expired
                    ? 'border-rose-200/80 bg-rose-50/60 dark:border-rose-500/25 dark:bg-rose-500/10'
                    : pct >= 65
                      ? 'border-amber-200/80 bg-amber-50/50 dark:border-amber-500/25 dark:bg-amber-500/10'
                      : 'border-emerald-200/70 bg-emerald-50/40 dark:border-emerald-500/20 dark:bg-emerald-500/10',
                className,
            )}
            role="timer"
            aria-live="polite"
            aria-label={t('v2.stock_approvals.countdown_title')}
        >
            <div className="mb-1.5 flex items-center justify-between gap-2">
                <span
                    className={cn(
                        'inline-flex items-center gap-1 text-gray-600 dark:text-zinc-300',
                        labelCls,
                    )}
                >
                    <PiClock className={cn(size === 'lg' ? 'size-4' : 'size-3.5')} />
                    {t('v2.stock_approvals.countdown_title')}
                </span>
                <span
                    className={cn(
                        'tabular-nums',
                        labelCls,
                        expired
                            ? 'text-rose-600 dark:text-rose-400'
                            : urgent
                              ? 'text-amber-700 dark:text-amber-300'
                              : 'text-emerald-700 dark:text-emerald-300',
                    )}
                >
                    {label}
                </span>
            </div>

            <div className={cn('w-full overflow-hidden rounded-full', barH, trackTone(pct, expired))}>
                <div
                    className={cn(
                        'h-full rounded-full transition-[width] duration-1000 ease-linear',
                        barTone(pct, expired),
                    )}
                    style={{ width: `${pct}%` }}
                />
            </div>

            {size !== 'sm' && (
                <p className="mt-1.5 text-[11px] text-gray-500 dark:text-zinc-400">
                    {t('v2.stock_approvals.countdown_until', {
                        date: formatExpiryDate(expiresAt),
                    })}
                </p>
            )}
        </div>
    );
}

function formatExpiryDate(value: string): string {
    try {
        return new Intl.DateTimeFormat(undefined, {
            dateStyle: 'short',
            timeStyle: 'short',
        }).format(new Date(value));
    } catch {
        return value;
    }
}
